<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Country Change Request</h4>
        <div class="text-muted small">Review request, risk indicators, and compliance flags</div>
      </div>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" (click)="goBack()">Back</button>
        <button class="btn btn-outline-primary btn-sm" [disabled]="loading" (click)="load()">Refresh</button>
      </div>
    </div>

    <div *ngIf="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div *ngIf="loading" class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <div class="placeholder-glow">
          <div class="placeholder col-4 mb-2"></div>
          <div class="placeholder col-6 mb-4"></div>
          <div class="placeholder col-12 mb-2"></div>
          <div class="placeholder col-10 mb-2"></div>
          <div class="placeholder col-8"></div>
        </div>
      </div>
    </div>

    <div *ngIf="request && !loading" class="row g-3">
      <div class="col-12 col-lg-8">
        <div class="card border-0 shadow-sm">
          <div class="card-body p-4">
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
              <div>
                <div class="fw-bold">Request Summary</div>
                <div class="text-muted small">Request ID: {{ request.id }}</div>
              </div>

              <span class="badge" [ngClass]="getStatusBadgeClass(request.status)">
                {{ request.status }}
              </span>
            </div>

            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="form-label small text-muted mb-1">User</label>
                <div class="fw-semibold">{{ getDisplayName() }}</div>
                <div class="small text-muted" *ngIf="request.email">{{ request.email }}</div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label small text-muted mb-1">Product Type</label>
                <div class="fw-semibold">{{ request.productType }}</div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label small text-muted mb-1">Current Country</label>
                <div class="fw-semibold">{{ request.currentCountryCode }}</div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label small text-muted mb-1">Requested Country</label>
                <div class="fw-semibold">{{ request.requestedCountryCode }}</div>
              </div>

              <div class="col-12">
                <label class="form-label small text-muted mb-1">Reason</label>
                <div class="border rounded p-3 bg-light-subtle">
                  {{ getDisplayReason() }}
                </div>
              </div>
            </div>

            <hr class="my-4" />

            <div class="fw-bold mb-2">Risk Assessment</div>
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Risk Level</label>
                <div>
                  <span class="badge" [ngClass]="getRiskBadgeClass(request.riskLevel)">
                    {{ request.riskLevel || 'N/A' }}
                    <ng-container *ngIf="request.riskScore !== undefined && request.riskScore !== null">
                      ({{ request.riskScore }})
                    </ng-container>
                  </span>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Manual Review</label>
                <div class="fw-semibold">
                  {{ request.requiresManualReview ? 'Required' : 'No' }}
                </div>
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Blocked Automatically</label>
                <div class="fw-semibold">
                  {{ request.isBlockedAutomatically ? 'Yes' : 'No' }}
                </div>
              </div>

              <div class="col-12">
                <label class="form-label small text-muted mb-1">Risk Flags</label>
                <div class="border rounded p-3 bg-light-subtle">
                  <ul class="mb-0 ps-3" *ngIf="parseRiskFlags(request.riskFlagsJson).length > 0; else noRiskFlags">
                    <li *ngFor="let flag of parseRiskFlags(request.riskFlagsJson)">
                      {{ flag }}
                    </li>
                  </ul>
                  <ng-template #noRiskFlags>
                    <span class="text-muted">No risk flags recorded</span>
                  </ng-template>
                </div>
              </div>
            </div>

            <hr class="my-4" />

            <div class="fw-bold mb-2">Audit</div>
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Requested At</label>
                <div class="fw-semibold">{{ getDisplayCreatedAt() | date:'medium' }}</div>
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Reviewed At</label>
                <div class="fw-semibold">
                  {{ request.reviewedAt ? (request.reviewedAt | date:'medium') : 'Not reviewed yet' }}
                </div>
              </div>

              <div class="col-12 col-md-4">
                <label class="form-label small text-muted mb-1">Applied At</label>
                <div class="fw-semibold">
                  {{ request.appliedAt ? (request.appliedAt | date:'medium') : 'Not applied yet' }}
                </div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label small text-muted mb-1">Reviewed By</label>
                <div class="fw-semibold">{{ getDisplayReviewedBy() }}</div>
              </div>

              <div class="col-12">
                <label class="form-label small text-muted mb-1">Admin Notes</label>
                <div class="border rounded p-3 bg-light-subtle">
                  {{ getDisplayNotes() }}
                </div>
              </div>
            </div>

            <hr class="my-4" />

            <div class="fw-bold mb-2">Compliance Flags</div>
            <div class="table-responsive border rounded">
              <table class="table table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Reason</th>
                    <th>Risk</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let flag of complianceFlags">
                    <td>{{ flag.reason }}</td>
                    <td>{{ flag.riskLevel }}</td>
                    <td>{{ flag.status }}</td>
                    <td>{{ flag.createdAt | date:'short' }}</td>
                  </tr>
                  <tr *ngIf="complianceFlags.length === 0">
                    <td colspan="4" class="text-center text-muted p-3">
                      No compliance flags for this user
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-4">
        <div class="card border-0 shadow-sm">
          <div class="card-body p-4">
            <div class="fw-bold mb-2">Review Actions</div>
            <div class="text-muted small mb-3">
              Approve or reject this request after verification.
            </div>

            <div *ngIf="request.status !== 'Pending'" class="alert alert-secondary small mb-0">
              This request has already been {{ request.status.toLowerCase() }}.
            </div>

            <div *ngIf="request.status === 'Pending'">
              <div class="mb-3">
                <label class="form-label small text-muted">Approval Notes</label>
                <textarea
                  class="form-control"
                  rows="3"
                  [(ngModel)]="approveNotes"
                  name="approveNotes"></textarea>
              </div>

              <div class="d-grid mb-3">
                <button class="btn btn-success btn-sm" [disabled]="acting" (click)="approve()">
                  <span *ngIf="!acting">Approve Request</span>
                  <span *ngIf="acting">Processing...</span>
                </button>
              </div>

              <hr />

              <div class="mb-3">
                <label class="form-label small text-muted">Rejection Notes</label>
                <textarea
                  class="form-control"
                  rows="3"
                  [(ngModel)]="rejectNotes"
                  name="rejectNotes"
                  placeholder="Explain why this request is being rejected"></textarea>
              </div>

              <div class="d-grid">
                <button class="btn btn-outline-danger btn-sm" [disabled]="acting" (click)="reject()">
                  Reject Request
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mt-3">
          <div class="card-body p-4">
            <div class="fw-bold mb-2">Compliance Note</div>
            <div class="small text-muted">
              Risk score, risk flags, and compliance flags are shown here to help admins decide whether the country change should be approved, rejected, or escalated.
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</div>